import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { withRouter } from 'next/router';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';

import Close from '../../../assets/svg/Close';
import DefaultButton from '../Buttons/DefaultButton';
import FilterGroup from '../Filters/FilterGroup';
import { BaseModalContainer } from './ModalContainers/ModalContainers';
import TrackingContext from '../../../tracking/TrackingContext';

import { getFilterFromName } from '../../../utils/selectors/filters';
import {
  generateQueryString,
  removeQueryAndHash,
} from '../../../utils/urls/urlGenerator';
import HandOffLink from '../../../utils/urls/HandOffLink';

import colors from '../../../styling/colors';

const ModalContainer = styled(BaseModalContainer)`
  display: flex;
  flex-flow: column;
  width: 100%;
  height: 100%;
`;

const Filters = styled.div`
  background-color: ${colors('dove')};
  flex: 0 1 auto;
  height: 100%;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
`;

const FilterActions = styled.div`
  padding: 1.25rem 1.5rem;
  display: flex;
  font-weight: 500;
  flex: 1 0 auto;
  align-items: center;
  width: 100%;
  box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.25);
  z-index: 1;

  @media (min-width: 30rem) {
    padding: 1.25rem 2rem;
  }
`;

const FilterAction = styled.div`
  flex: 1;
`;

const FilterActionClose = styled(FilterAction)`
  height: 1rem;
`;

const FilterActionTitle = styled(FilterAction)`
  font-size: 1rem;
  text-align: center;
  line-height: 1.5rem;

  @media (min-width: 30rem) {
    font-size: 1.25rem;
  }
`;

const FilterActionClear = styled(FilterAction)`
  text-align: right;
`;

const FilterActionLink = styled(HandOffLink)`
  font-weight: 400;
  color: ${colors('gatsby')};
`;

const BottomArea = styled.div`
  box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  flex: 1 0 auto;
  width: 100%;
  padding: 1.25rem 1.5rem;
  z-index: 1;
`;

const ViewDesignButton = styled(DefaultButton)`
  max-width: 22.5rem;
  width: 100%;
`;

class FilterModal extends Component {
  static propTypes = {
    sort: PropTypes.string,
    filterGroups: PropTypes.array.isRequired,
    closeAction: PropTypes.func.isRequired,
    totalSelected: PropTypes.number.isRequired,
  };

  render() {
    const {
      modalType,
      tab,
      isMobile,
      sort,
      filterGroups,
      closeAction,
      totalSelected,
      router: {
        asPath,
        query: { category },
      },
      isUPLP,
    } = this.props;

    // Separating this out into a container will reduce repaints significantly
    let sortCategories = {
      name: 'Sort by',
      slug: 'sort',
      values: [
        {
          name: 'Most Popular',
          on: false,
          type: 'sort',
          value: 'Most Popular',
        },
        {
          name: 'Recently Added',
          on: false,
          type: 'sort',
          value: 'Recently Added',
        },
        { name: 'Price (Low)', on: false, type: 'sort', value: 'Price (Low)' },
        {
          name: 'Price (High)',
          on: false,
          type: 'sort',
          value: 'Price (High)',
        },
      ],
    };
    let sortGroup = null;

    if (sort) {
      let sortValue = sort === 'Relevance' ? 'Most Popular' : sort;
      getFilterFromName(sortCategories, sortValue).on = true;

      sortGroup = (
        <FilterGroup
          filterGroupData={sortCategories}
          filterType="sort"
          isMobile={isMobile}
        />
      );
    }

    const resetQueryString = generateQueryString({ tab, category });

    return (
      <TrackingContext.Provider value={{ filterSelectionType: modalType }}>
        <ModalContainer>
          <FilterActions>
            <FilterActionClose onClick={() => closeAction('close-x')}>
              <Close width="1rem" height="1rem" />
            </FilterActionClose>
            <FilterActionTitle>Filters</FilterActionTitle>
            <FilterActionClear>
              <FilterActionLink
                identifier={`reset-filters-${tab}`}
                href={`${removeQueryAndHash(asPath)}${resetQueryString}`}
              >
                Clear
              </FilterActionLink>
            </FilterActionClear>
          </FilterActions>
          <Filters>
            {sortGroup}
            {filterGroups.map((filterGroupData, groupIndex) => {
              if (filterGroupData.slug === 'features') return null;
              return (
                <FilterGroup
                  key={filterGroupData.slug}
                  filterGroupData={filterGroupData}
                  filterType="filter"
                  isMobile={isMobile}
                />
              );
            })}
          </Filters>
          <BottomArea>
            <ViewDesignButton
              color="gatsby"
              onClick={() => closeAction(`close-view-designs`)}
            >
              View {pluralize('Design', totalSelected)} ({totalSelected})
            </ViewDesignButton>
          </BottomArea>
        </ModalContainer>
      </TrackingContext.Provider>
    );
  }
}

FilterModal = withRouter(FilterModal);

const cardFilterMapStateToProps = ({
  env: { isMobile },
  packages: { total },
  modals: { isUPLP },
  filters: {
    cardFilters: { sort, filterGroups },
  },
}) => ({
  modalType: 'cardFilters',
  tab: isUPLP ? 'cards' : undefined,
  isMobile,
  sort,
  filterGroups,
  totalSelected: total,
  isUPLP,
});

const CardFilterModal = connect(cardFilterMapStateToProps)(FilterModal);

const flyerFilterMapStateToProps = ({
  env: { isMobile },
  modals: { isUPLP },
  filters: {
    flyerFilters: { filterGroups, flyerResults },
  },
}) => ({
  modalType: 'flyerFilters',
  tab: isUPLP ? 'flyers' : undefined,
  isMobile,
  sort: undefined,
  filterGroups,
  totalSelected: flyerResults,
  isUPLP,
});

const FlyerFilterModal = connect(flyerFilterMapStateToProps)(FilterModal);

export { CardFilterModal, FlyerFilterModal };
